import api from './api';
import {
  setHydratedFeatureFlags,
  type ClientFeatureFlags,
  getClientFeatureFlags,
} from '../config/featureFlags';

export type NotificationRecord = {
  id: string;
  companyId: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  linkPath?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  isRead: boolean;
  readAt?: string | null;
  createdAt: string;
};

export type NotificationListResponse = {
  data: NotificationRecord[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  unreadCount: number;
};

export type NotificationPreferences = {
  companyId?: string;
  userId?: string;
  inAppEnabled: boolean;
  emailEnabled: boolean;
  mutedTypes?: string[] | null;
};

async function list(params: {
  page?: number;
  pageSize?: number;
  unreadOnly?: boolean;
  type?: string;
  search?: string;
} = {}): Promise<NotificationListResponse> {
  const res = await api.get<NotificationListResponse>('/notifications', { params });
  return res.data;
}

async function unreadCount(): Promise<number> {
  const res = await api.get<{ unreadCount: number }>('/notifications/unread-count');
  return res.data.unreadCount;
}

async function markRead(id: string): Promise<NotificationRecord> {
  const res = await api.patch<NotificationRecord>(`/notifications/${id}/read`);
  return res.data;
}

async function markAllRead(): Promise<{ updated: number }> {
  const res = await api.patch<{ updated: number }>('/notifications/read-all');
  return res.data;
}

async function remove(id: string): Promise<void> {
  await api.delete(`/notifications/${id}`);
}

async function getPreferences(): Promise<NotificationPreferences> {
  const res = await api.get<NotificationPreferences>('/notifications/preferences');
  return res.data;
}

async function updatePreferences(
  body: Partial<NotificationPreferences>,
): Promise<NotificationPreferences> {
  const res = await api.patch<NotificationPreferences>('/notifications/preferences', body);
  return res.data;
}

async function hydrateFeatureFlags(): Promise<ClientFeatureFlags> {
  try {
    const res = await api.get<ClientFeatureFlags>('/feature-flags/public');
    setHydratedFeatureFlags(res.data);
    return getClientFeatureFlags();
  } catch {
    return getClientFeatureFlags();
  }
}

const notificationsService = {
  list,
  unreadCount,
  markRead,
  markAllRead,
  remove,
  getPreferences,
  updatePreferences,
  hydrateFeatureFlags,
};

export default notificationsService;
